import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { TitleCasePipe, formatDate } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import {
  BreadcrumbComponent,
  BreadcrumbLevel,
} from '../../../../../shared/components/breadcrumb/breadcrumb.component';
import { SpinnerComponent } from '../../../../../shared/components/az-spinner/spinner.component';
import {
  JOB_STATUSES_NEW_TAGS,
  LANGUAGES,
} from '../../../../../shared/config/constants';
import { S3FilesService } from '../../../../../shared/services/s3files.service';
import { TranslationFileType } from '../../../../../shared/models/s3-destination-folder';
import { VendorOrder } from '../../../../../shared/models/vendor-order.model';
import { VendorOrdersService } from '../services/vendor-orders.service';
import { VendorService } from '../../../../../shared/services/vendor.service';
import { NotificationService } from '../../../../../shared/services/notification.service';
import { QuotesComponent } from '../quotes/quotes.component';
import { TranslatedFilesComponent } from '../translated-files/translated-files.component';
import { ToolbarTitleService } from '../../../components/toolbar/toolbar-title.service';

/** Represents a source file with its translation type classification. */
interface SourceFile {
  name: string;
  size: string;
  bytes: number;
}

type FileSortColumn = 'name' | 'size';

interface FileSortState {
  column: FileSortColumn;
  descending: boolean;
}

type FileTableType = 'basic' | 'dtp' | 'reference';
type JobDetailTabId = 'details' | 'quotes' | 'translated-files';

interface JobDetailTab {
  id: JobDetailTabId;
  label: string;
}

/** View model for the job detail page. */
interface JobDetailView {
  jobUid: string;
  jobNumber: string;
  jobName: string;
  requestDate: string;
  targetDate: string;
  statusLabel: string;
  statusKey: string;
  statusIcon: string | null;
  contentType: string;
  therapeuticArea: string;
  costCentre: string;
  targetAudience: string;
  translationCertificate: string;
  documentTone: string;
  additionalInfo: string;
  desktopPublishingRequirement: string;
  sourceLanguage: string;
  targetLanguages: string[];
  basicFormatFiles: SourceFile[];
  dtpFiles: SourceFile[];
  referenceFiles: SourceFile[];
}

const LOCALE_LOOKUP = new Map<string, string>(Object.entries(LANGUAGES));

const STATUS_ICON_MAP: Readonly<Record<string, string>> = {
  'Estimated Quotes Pending': 'schedule',
  'Final Quote Pending': 'schedule',
  'Final Quote to be Reviewed': 'request_quote',
  'In Progress': 'article_person',
  Cancelled: 'cancel',
  Completed: 'check_circle',
  Rejected: 'do_not_disturb_on',
};

@Component({
  selector: 'app-job-detail',
  standalone: true,
  imports: [
    BreadcrumbComponent,
    MatIconModule,
    SpinnerComponent,
    TitleCasePipe,
    QuotesComponent,
    TranslatedFilesComponent,
    SpinnerComponent,
  ],
  templateUrl: './job-detail.component.html',
  styleUrl: './job-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobDetailComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly vendorOrdersService = inject(VendorOrdersService);
  private readonly vendorService = inject(VendorService);
  private readonly s3FilesService = inject(S3FilesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toolbarTitleService = inject(ToolbarTitleService);
  readonly notificationService = inject(NotificationService);

  private readonly order = signal<VendorOrder | null>(null);
  private readonly hasWindow = typeof window !== 'undefined';
  private readonly selectedFilters = signal<string[]>([]);
  private readonly notificationSnapshotOnEntry = signal('');
  private readonly sourceFiles = signal<SourceFile[]>([]);
  private readonly referenceFiles = signal<SourceFile[]>([]);
  readonly basicFilesSort = signal<FileSortState>({
    column: 'name',
    descending: false,
  });
  readonly dtpFilesSort = signal<FileSortState>({
    column: 'name',
    descending: false,
  });
  readonly referenceFilesSort = signal<FileSortState>({
    column: 'name',
    descending: false,
  });
  readonly isLoading = signal(true);
  readonly isFilesLoading = signal(false);
  readonly errorMessage = signal<string | null>(null);
  private static readonly TAB_FADE_DURATION_MS = 180;

  private fadeExitTimerId: ReturnType<typeof setTimeout> | null = null;
  private fadeEnterTimerId: ReturnType<typeof setTimeout> | null = null;

  readonly tabs = computed<readonly JobDetailTab[]>(() => {
    return [
      { id: 'details', label: 'Job Details' },
      { id: 'quotes', label: 'Quotes' },
      { id: 'translated-files', label: 'Translated Files' },
    ];
  });

  readonly activeTab = signal<JobDetailTabId>('details');
  readonly renderedTab = signal<JobDetailTabId>('details');
  readonly tabContentState = signal<'idle' | 'exiting' | 'entering'>('idle');
  readonly tabIndicatorWidth = signal(0);
  readonly tabIndicatorOffset = signal(0);
  readonly tabIndicatorTransform = computed(
    () => `translateX(${this.tabIndicatorOffset()}px)`,
  );

  readonly tabsNav = viewChild<ElementRef<HTMLElement>>('tabsNav');
  readonly tabButtons =
    viewChildren<ElementRef<HTMLButtonElement>>('tabButton');
  readonly breadcrumbLevels = computed<BreadcrumbLevel[]>(() => {
    const detail = this.jobDetail();
    const levels: BreadcrumbLevel[] = [
      {
        label: 'Translation Manager',
        route: '/translation-manager',
        state: {
          fromBreadcrumb: true,
          selectedFilters: this.selectedFilters(),
          notificationSnapshot: this.notificationSnapshotOnEntry(),
        },
      },
    ];

    if (detail?.jobUid) {
      levels.push({ label: detail.jobUid });
    }

    return levels;
  });

  readonly jobDetail = computed<JobDetailView | null>(() => {
    const order = this.order();
    if (!order) {
      return null;
    }
    return this.mapOrderToView(order);
  });
  readonly fullOrder = computed(() => this.order());

  ngOnInit(): void {
    this.selectedFilters.set(this.getSelectedFiltersFromState());
    this.notificationSnapshotOnEntry.set(
      this.notificationService.notificationSnapshotKey(),
    );

    const jobUid = this.route.snapshot.paramMap.get('jobUid');
    if (!jobUid) {
      this.errorMessage.set('Unable to open this job. Missing job ID.');
      this.isLoading.set(false);
      this.isFilesLoading.set(false);
      return;
    }
    this.loadOrder(jobUid);
  }

  ngAfterViewInit(): void {
    this.syncTabIndicator();
  }

  ngOnDestroy(): void {
    this.clearTabTransitionTimers();
    if (this.hasWindow) {
      window.removeEventListener('resize', this.handleWindowResize);
    }
    this.toolbarTitleService.clearContextualTitle();
  }

  setActiveTab(tab: JobDetailTabId): void {
    if (this.activeTab() === tab) {
      return;
    }

    this.activeTab.set(tab);
    this.syncTabIndicator();

    const jobUid = this.route.snapshot.paramMap.get('jobUid') ?? '';

    if (tab === 'quotes' && this.notificationService.isAwaitingVendorJob(jobUid)) {
      this.notificationService.acknowledgeVendorJob(jobUid);
    }

    if (tab === 'translated-files' && this.notificationService.isCompletedDocsJob(jobUid)) {
      this.notificationService.acknowledgeCompletedJob(jobUid);
    }

    this.clearTabTransitionTimers();
    this.tabContentState.set('exiting');

    this.fadeExitTimerId = setTimeout(() => {
      this.renderedTab.set(tab);
      this.tabContentState.set('entering');

      this.fadeEnterTimerId = setTimeout(() => {
        this.tabContentState.set('idle');
        this.fadeEnterTimerId = null;
      }, JobDetailComponent.TAB_FADE_DURATION_MS);

      this.fadeExitTimerId = null;
    }, JobDetailComponent.TAB_FADE_DURATION_MS);
  }

  onTabsKeydown(event: KeyboardEvent): void {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
      return;
    }

    event.preventDefault();

    const currentIndex = this.tabs().findIndex(
      (tab) => tab.id === this.activeTab(),
    );
    const delta = event.key === 'ArrowRight' ? 1 : -1;
    const nextIndex =
      (currentIndex + delta + this.tabs().length) % this.tabs().length;
    const nextTab = this.tabs()[nextIndex];

    this.setActiveTab(nextTab.id);
    this.tabButtons()[nextIndex]?.nativeElement.focus();
  }

  getTabAriaControls(tabId: JobDetailTabId): string {
    return `job-detail-tab-panel-${tabId}`;
  }

  getTabId(tabId: JobDetailTabId): string {
    return `job-detail-tab-${tabId}`;
  }

  getRenderedTabPanelLabel(): string {
    return this.tabs().find((tab) => tab.id === this.renderedTab())?.label ?? '';
  }

  handleWindowResize = (): void => {
    this.syncTabIndicator();
  };

  private clearTabTransitionTimers(): void {
    if (this.fadeExitTimerId) {
      clearTimeout(this.fadeExitTimerId);
      this.fadeExitTimerId = null;
    }

    if (this.fadeEnterTimerId) {
      clearTimeout(this.fadeEnterTimerId);
      this.fadeEnterTimerId = null;
    }
  }

  private syncTabIndicator(): void {
    requestAnimationFrame(() => {
      const navElement = this.tabsNav()?.nativeElement;
      const activeIndex = this.tabs().findIndex(
        (tab) => tab.id === this.activeTab(),
      );
      const activeButtonElement = this.tabButtons()[activeIndex]?.nativeElement;

      if (!navElement || !activeButtonElement) {
        this.tabIndicatorWidth.set(0);
        this.tabIndicatorOffset.set(0);
        return;
      }

      const navRect = navElement.getBoundingClientRect();
      const activeRect = activeButtonElement.getBoundingClientRect();

      this.tabIndicatorOffset.set(activeRect.left - navRect.left);
      this.tabIndicatorWidth.set(activeRect.width);
    });
  }

  constructor() {
    if (this.hasWindow) {
      window.addEventListener('resize', this.handleWindowResize);
    }

    effect(() => {
      this.tabButtons();
      this.activeTab();
      this.syncTabIndicator();
    });
  }

  toggleFileSort(table: FileTableType, column: FileSortColumn): void {
    this.getSortSignal(table).update((state) => {
      if (state.column === column) {
        return { ...state, descending: !state.descending };
      }

      return {
        column,
        descending: false,
      };
    });
  }

  getFileAriaSort(
    table: FileTableType,
    column: FileSortColumn,
  ): 'ascending' | 'descending' | null {
    const state = this.getSortSignal(table)();

    if (state.column !== column) {
      return null;
    }

    return state.descending ? 'descending' : 'ascending';
  }

  navigateBack(): void {
    this.router.navigate(['/translation-manager'], {
      state: {
        fromDetail: true,
        selectedFilters: this.selectedFilters(),
        notificationSnapshot: this.notificationSnapshotOnEntry(),
      },
    });
  }

  onQuoteConfirmed(jobUid: string): void {
    this.loadOrder(jobUid, true);
  }

  private loadOrder(jobUid: string, forceRefresh = false): void {
    this.isLoading.set(true);
    this.errorMessage.set(null);
    this.sourceFiles.set([]);
    this.referenceFiles.set([]);

    this.vendorOrdersService
      .getVendorOrderByUid(jobUid, forceRefresh)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (order) => {
          if (order) {
            this.order.set(order);
            this.toolbarTitleService.setContextualTitle(
              this.buildRequestToolbarTitle(order.jobNumber, order.jobUid),
            );
            void this.loadFolderFiles(jobUid, order.isDtpJob);
          } else {
            this.errorMessage.set('Translation job not found.');
            this.isFilesLoading.set(false);
            this.toolbarTitleService.clearContextualTitle();
          }
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
          this.isFilesLoading.set(false);
          this.toolbarTitleService.clearContextualTitle();
          this.errorMessage.set(
            'Unable to load job details right now. Please try again.',
          );
        },
      });
  }

  private mapOrderToView(order: VendorOrder): JobDetailView {
    const sourceFiles = this.sourceFiles();
    const referenceFiles = this.referenceFiles();
    const isDtpJob = order.isDtpJob === true;
    const basicFormatFiles = isDtpJob
      ? []
      : this.sortFiles(sourceFiles, this.basicFilesSort());
    const dtpFiles = isDtpJob
      ? this.sortFiles(sourceFiles, this.dtpFilesSort())
      : [];

    const statusLabel = this.getStatusLabel(order.jobStatus);

    return {
      jobUid: order.jobUid ?? '--',
      jobNumber: order.jobNumber ?? '--',
      jobName: order.customJobName ?? '--',
      requestDate: this.formatDateValue(order.createdAt),
      targetDate: this.formatDateValue(this.getTargetDate(order)),
      statusLabel,
      statusKey: this.getStatusKey(statusLabel),
      statusIcon: this.getStatusIcon(statusLabel),
      contentType: order.contentType ?? '--',
      therapeuticArea: order.therapeuticArea ?? '--',
      costCentre: order.costCentre ?? '--',
      targetAudience: order.targetAudience ?? '--',
      translationCertificate: order.translationCertificateRequired
        ? 'Yes'
        : 'No',
      documentTone: order.documentTone ?? '--',
      additionalInfo: order.additionalInfo || '',
      desktopPublishingRequirement: order.desktopPublishingRequirement ?? '--',
      sourceLanguage: this.getLocaleName(order.sourceLocaleId),
      targetLanguages: this.getTargetLanguages(order.targetLocaleId),
      basicFormatFiles,
      dtpFiles,
      referenceFiles: this.sortFiles(referenceFiles, this.referenceFilesSort()),
    };
  }

  private async loadFolderFiles(
    jobUid: string,
    isDtpJob?: boolean,
  ): Promise<void> {
    if (!jobUid) {
      this.sourceFiles.set([]);
      this.referenceFiles.set([]);
      this.isFilesLoading.set(false);
      return;
    }

    this.isFilesLoading.set(true);

    try {
      const [sourceResponse, referenceResponse] = await Promise.all([
        this.s3FilesService.getFilesInFolderWithSize(
          TranslationFileType.SOURCE,
          jobUid,
        ),
        this.s3FilesService.getFilesInFolderWithSize(
          TranslationFileType.REFERENCE,
          jobUid,
        ),
      ]);

      const mappedSourceFiles = (sourceResponse?.files ?? []).map((file) => ({
        name: file.name,
        size: this.formatBytes(file.size),
        bytes: file.size,
      }));

      const mappedReferenceFiles = (referenceResponse?.files ?? []).map(
        (file) => ({
          name: file.name,
          size: this.formatBytes(file.size),
          bytes: file.size,
        }),
      );

      const filteredReferenceFiles = this.filterReferenceDuplicates(
        mappedSourceFiles,
        mappedReferenceFiles,
      );

      this.sourceFiles.set(mappedSourceFiles);
      this.referenceFiles.set(filteredReferenceFiles);

      if (isDtpJob !== undefined) {
        this.order.update((order) => (order ? { ...order, isDtpJob } : order));
      }
    } catch {
      this.sourceFiles.set([]);
      this.referenceFiles.set([]);
    } finally {
      this.isFilesLoading.set(false);
    }
  }

  private buildRequestToolbarTitle(
    jobNumber?: string,
    fallbackUid?: string,
  ): string | null {
    const cleanedJobNumber = jobNumber?.replace(/^AZT-/i, '').trim() ?? '';

    if (cleanedJobNumber.length > 0) {
      return `Request ${cleanedJobNumber}`;
    }

    const cleanedFallbackUid = fallbackUid?.trim() ?? '';
    if (cleanedFallbackUid.length > 0) {
      return `Request ${cleanedFallbackUid}`;
    }

    return null;
  }

  private getSelectedFiltersFromState(): string[] {
    const state = history.state as { selectedFilters?: string[] } | null;
    if (!Array.isArray(state?.selectedFilters)) {
      return [];
    }

    return state.selectedFilters.filter(
      (value) => typeof value === 'string' && value.trim().length > 0,
    );
  }

  private formatBytes(bytes: number): string {
    if (!Number.isFinite(bytes)) {
      return '--';
    }

    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex += 1;
    }

    return `${this.formatDecimal(size)} ${units[unitIndex]}`;
  }

  private formatDecimal(value: number): string {
    if (Number.isInteger(value)) {
      return value.toString();
    }

    const trimmed = value
      .toFixed(2)
      .replace(/\.0+$/, '')
      .replace(/(\.\d*?)0+$/, '$1');
    return trimmed;
  }

  private filterReferenceDuplicates(
    sourceFiles: SourceFile[],
    referenceFiles: SourceFile[],
  ): SourceFile[] {
    const sourceCounts = new Map<string, number>();
    for (const file of sourceFiles) {
      const key = file.name.toLowerCase();
      sourceCounts.set(key, (sourceCounts.get(key) ?? 0) + 1);
    }

    const filtered: SourceFile[] = [];
    for (const file of referenceFiles) {
      const key = file.name.toLowerCase();
      const remaining = sourceCounts.get(key) ?? 0;
      if (remaining > 0) {
        sourceCounts.set(key, remaining - 1);
        continue;
      }
      filtered.push(file);
    }

    return filtered;
  }

  private sortFiles(
    files: SourceFile[],
    sortState: FileSortState,
  ): SourceFile[] {
    const direction = sortState.descending ? -1 : 1;

    return [...files].sort((left, right) => {
      if (sortState.column === 'name') {
        return (
          left.name.localeCompare(right.name, undefined, {
            sensitivity: 'base',
          }) * direction
        );
      }

      return (left.bytes - right.bytes) * direction;
    });
  }

  private getSortSignal(table: FileTableType) {
    switch (table) {
      case 'basic':
        return this.basicFilesSort;
      case 'dtp':
        return this.dtpFilesSort;
      default:
        return this.referenceFilesSort;
    }
  }

  private getTargetDate(order: VendorOrder): string | undefined {
    const estimatedDate = order.estimatedDeliveryDate;
    const fixedDate = order.fixedDeliveryDate;
    let targetDate = order.deadline;

    if (estimatedDate) {
      targetDate = estimatedDate;
    }

    if (fixedDate && fixedDate !== estimatedDate) {
      targetDate = fixedDate;
    }

    return targetDate;
  }

  private formatDateValue(value?: number | string): string {
    if (value === undefined || value === null) {
      return '--';
    }

    const dateValue =
      typeof value === 'number' ? new Date(value) : new Date(value);
    if (Number.isNaN(dateValue.getTime())) {
      return '--';
    }

    return formatDate(dateValue, 'dd/MMM/yyyy', 'en-GB');
  }

  private getLocaleName(localeId?: string): string {
    if (!localeId) {
      return '--';
    }
    return LOCALE_LOOKUP.get(localeId) ?? localeId;
  }

  private getTargetLanguages(localeIds?: string[]): string[] {
    if (!localeIds || localeIds.length === 0) {
      return [];
    }
    return localeIds.map((id) => this.getLocaleName(id));
  }

  private getStatusLabel(status?: string): string {
    if (!status) {
      return '--';
    }
    const mapped =
      JOB_STATUSES_NEW_TAGS[status as keyof typeof JOB_STATUSES_NEW_TAGS];
    return mapped ?? this.formatFallbackStatus(status);
  }

  private formatFallbackStatus(status: string): string {
    return status
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (m) => m.toUpperCase());
  }

  private getStatusKey(statusLabel?: string): string {
    if (!statusLabel || statusLabel === '--') {
      return 'unknown';
    }
    return statusLabel
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  private getStatusIcon(statusLabel?: string): string | null {
    if (!statusLabel || statusLabel === '--') {
      return null;
    }
    return STATUS_ICON_MAP[statusLabel] ?? null;
  }
}
